import { EConfigurationNames } from '@ConfigLoader/ConfigFiles';
import ConfigLoader from '@ConfigLoader/ConfigLoader';
import { GameComponent } from '@Core/GameComponent/GameComponent';
import { GameScene } from '@Core/GameScene/GameScene';
import ReelSymbol from '@Core/Reels/ReelSymbol';
import Symbol from '@Core/Reels/Symbol';
import { ESymbolAnimations } from '@Core/Reels/SymbolAnimations';
import { ESymbolRoles } from '@Core/Reels/SymbolRoles';
import { TextContainerConfig } from '@Core/TextContainer/TextContainer.types';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';
import { Point } from 'pixi.js';

export class StickySymbols extends GameComponent {
    public stickySymbols: Symbol[][] = [];
    constructor(public readonly scene: GameScene) {
        super(scene);
        this.scene = scene;

        Logger.log(LoggerLevel.Constructor, { class: StickySymbols.name, source: LoggerSource.Game, message: 'StickySymbols constructor' });
        EventsCenter.on(EGameEvents.RESIZED_REELS, this.onReelsResize, this);
    }

    public getStickySymbolKey(): string {
        let symbolKeys: string[] = [];
        this.gameStore.get(EGameStoreKeys.AuthenticateGameConfig).symbols[0].forEach((symbolConfig) => {
            const roles = symbolConfig.roles;
            if (!roles) return;
            for (const role of roles) {
                if (role.types.includes(ESymbolRoles.LOCK_AND_ROLL_COIN)) {
                    const symbolData = this.scene.game.gameStore.get(EGameStoreKeys.SymbolConfiguration).find((symbol) => symbol.id === symbolConfig.symbolId)!;
                    if (symbolData) symbolKeys.push(symbolData.asset.dataKey);
                }
            }
        });
        return symbolKeys[0];
    }

    public onCreate(): void {
        const symbolKey = this.getStickySymbolKey();

        if (!symbolKey) return;

        for (let i = 0; i < this.gameStore.get(EGameStoreKeys.NumberOfReels); i++) {
            if (!this.stickySymbols[i]) this.stickySymbols[i] = [];
        }
    }

    private onReelsResize(): void {
        const { scalesPerReel, defaultSymbolContainerPositions } = this.gameStore.get(EGameStoreKeys.ReelParameters);
        const numberOfReels = this.scene.game.gameStore.get(EGameStoreKeys.NumberOfReels);
        const numberOfSymbols = this.scene.game.gameStore.get(EGameStoreKeys.NumberOfSymbolsPerReel);
        if (!this.scene.reels.reelElements || this.scene.reels.reelElements.length === 0) return;

        for (let i = 0; i < numberOfReels; i++) {
            if (!this.stickySymbols[i]) continue;

            for (let j = 0; j < numberOfSymbols; j++) {
                const point = defaultSymbolContainerPositions[i][j + 1];
                if (!point || !this.stickySymbols[i][j]) continue;
                this.stickySymbols[i][j].position = new Point(point.x, point.y);
                this.stickySymbols[i][j].scale = new Point(scalesPerReel[i].x, scalesPerReel[i].y);
            }
        }
    }

    public async lockSymbol(symbol: ReelSymbol): Promise<void> {
        const { reelIndex, symbolIndex, symbolLabels } = symbol;
        this.createStickySymbol(symbol);
        const stickySymbol = this.stickySymbols[reelIndex][symbolIndex - 1];
        const animation = stickySymbol.symbolAnimation;
        if (symbolLabels.size > 0) {
            const coinLabelConfig = ConfigLoader.getConfiguration(EConfigurationNames.TEXTS).styles?.['lock_and_roll_coin_label'] as TextContainerConfig;
            symbolLabels.forEach((label, slotName) => {
                stickySymbol.setSymbolLabel(label.getText(), slotName, coinLabelConfig);
            });
        }
        animation.spine.visible = true;

        this.onReelsResize();

        const hasLockAnimation = animation.eventManager.hasGameEventAnimation(ESymbolAnimations.SYMBOL_LOCK);
        const hasLockedAnimation = animation.eventManager.hasGameEventAnimation(ESymbolAnimations.SYMBOL_LOCKED);
        const hasWinAnimation = animation.eventManager.hasGameEventAnimation(ESymbolAnimations.WIN);

        if (hasLockAnimation) {
            animation.eventManager.playEventAnimation(ESymbolAnimations.SYMBOL_LOCK);
            await animation.waitForAnimationEvent(ESymbolAnimations.SYMBOL_LOCK, 'complete').promise;
        }

        if (hasLockedAnimation) {
            animation.eventManager.playEventAnimation(ESymbolAnimations.SYMBOL_LOCKED);
        } else if (hasWinAnimation) {
            animation.eventManager.playEventAnimation(ESymbolAnimations.WIN, undefined, undefined, undefined, undefined, true);
        }
    }

    public async unlockSymbol(symbol: ReelSymbol): Promise<void> {
        const { reelIndex, symbolIndex } = symbol;
        const stickySymbol = this.stickySymbols[reelIndex][symbolIndex - 1];
        const animation = stickySymbol.symbolAnimation;

        if (stickySymbol.symbolLabels.size > 0) {
            stickySymbol.removeSymbolLabels();
        }

        if (animation.eventManager.hasGameEventAnimation(ESymbolAnimations.SYMBOL_UNLOCK)) {
            animation.eventManager.playEventAnimation(ESymbolAnimations.SYMBOL_UNLOCK);
            await animation.waitForAnimationEvent(ESymbolAnimations.SYMBOL_UNLOCK, 'complete').promise;
        }

        animation.spine.skeleton.setupPose();
        animation.spine.state.clearTracks();
        animation.spine.visible = false;
        delete this.stickySymbols[reelIndex][symbolIndex - 1];
    }

    public createStickySymbol(reelSymbol: ReelSymbol): void {
        const { reel, symbolSpineConfig, symbolId, reelIndex, symbolIndex } = reelSymbol;
        const stickySymbol = new Symbol(reel, this.scene, { x: 0, y: 0 }, symbolSpineConfig, symbolId, reelIndex, symbolIndex);
        this.stickySymbols[reelIndex][symbolIndex - 1] = stickySymbol;
        const container = this.scene.reels.stickySymbolsLayer;
        container.addChild(stickySymbol);
        stickySymbol.symbolAnimation.spine.visible = false;
    }
}
